import {
  TIndirectTaxApiTransactionExtended,
  TIndirectTaxCalculationTaxBreakdownJurisdictionType,
  TIndirectTaxLineItemTaxBreakdownApi,
  TIndirectTaxLineItemTaxLogicApi,
  TIndirectTaxManualUploadSourcePlatform,
  TIndirectTaxTransactionFilterSourcePlatform,
  TIndirectTaxTransactionLineItemApi,
  TIndirectTaxTransactionListItem,
  TIndirectTaxTransactionSourcePlatform,
} from '@commenda/api/indirect-tax/transactions/types';
import {
  NonTimezoneSensitiveDate,
  ROOTFI_INTEGRATION_ENUM_TO_TEXT,
  toTitleCaseSentence,
} from '@commenda/utils';
import { Country, State } from 'country-state-city';

import {
  IndirectTaxRegion,
  IndirectTaxRegionHelper,
  IndirectTaxSourcePlatformHelper,
} from '../../common';
import {
  TIndirectTaxJurisdictionBreakdown,
  TIndirectTaxLineItemTaxLogic,
  TIndirectTaxTaxBasis,
  TIndirectTaxTransaction,
  TIndirectTaxTransactionDetail,
  TIndirectTaxTransactionLineItem,
  TIndirectTaxTransactionStatus,
  TIndirectTaxTransactionSupplyType,
  TIndirectTaxTransactionType,
} from '../types';

export class IndirectTaxTransactionMapper {
  private static getCountryOrTradeBlocDisplay(countryCode: string): string {
    const region =
      IndirectTaxRegionHelper.getRegionFromCountryCode(countryCode);
    const map: Record<IndirectTaxRegion, string> = {
      [IndirectTaxRegion.ALL_REGIONS]: countryCode,
      [IndirectTaxRegion.ASIA_PACIFIC]: countryCode,
      [IndirectTaxRegion.MIDDLE_EAST]: countryCode,
      [IndirectTaxRegion.LATIN_AMERICA]: countryCode,
      [IndirectTaxRegion.UNSUPPORTED]: countryCode,
      [IndirectTaxRegion.UNITED_STATES]: 'United States',
      [IndirectTaxRegion.EUROPEAN_UNION]: 'European Union',
      [IndirectTaxRegion.UNITED_KINGDOM]: 'United Kingdom',
      [IndirectTaxRegion.CANADA]: 'Canada',
    };
    return map[region];
  }

  private static getJurisdictionName(
    stateCode: string,
    countryCode: string
  ): string {
    if (!stateCode) return '';
    // Resolve the ISO 3166-2 subdivision code to a human-readable name for every
    // country, not just US/CA. country-state-city carries subdivision data for
    // GB/DE/IN/JP/IT/IE/etc., so codes like "ENG", "KA", "BY" humanize correctly.
    // Falls back to the raw code for anything the library doesn't know.
    const state = State.getStateByCodeAndCountry(stateCode, countryCode);
    return state?.name ?? stateCode;
  }

  private static getCountryFullName(countryCode: string | undefined): string {
    if (!countryCode) return '';
    const country = Country.getCountryByCode(countryCode);
    return country?.name ?? countryCode;
  }

  // Supply type is relative to the home region of the view it's shown in:
  // the active region tab on the transactions page, or the filing's region in
  // the filings transaction modal (UK tab/filing → UK is home; EU tab/filing →
  // the EU bloc is home). The same Italy→UK transaction is an Export from the
  // EU's perspective but an Import from the UK's — so it must be classified
  // against the home region, not against whichever bloc the origin happens to
  // belong to.
  private static deriveSupplyType(
    originCountryCode: string | undefined,
    destinationCountryCode: string | undefined,
    homeRegion: IndirectTaxRegion
  ): TIndirectTaxTransactionSupplyType | undefined {
    if (!originCountryCode || !destinationCountryCode) return undefined;

    const originInHome = IndirectTaxRegionHelper.isCountryInRegion(
      originCountryCode,
      homeRegion
    );
    const destInHome = IndirectTaxRegionHelper.isCountryInRegion(
      destinationCountryCode,
      homeRegion
    );

    if (originInHome && destInHome) {
      // The EU is a bloc, so two different member states are intra-community;
      // the UK is a single country, so only same-country pairs reach here.
      if (
        homeRegion === IndirectTaxRegion.EUROPEAN_UNION &&
        originCountryCode !== destinationCountryCode
      ) {
        return TIndirectTaxTransactionSupplyType.INTRA_COMMUNITY;
      }
      return TIndirectTaxTransactionSupplyType.DOMESTIC;
    }

    if (originInHome && !destInHome) {
      return TIndirectTaxTransactionSupplyType.EXPORT;
    }

    if (!originInHome && destInHome) {
      return TIndirectTaxTransactionSupplyType.IMPORT;
    }

    // Neither side touches the home region — out of scope for this jurisdiction.
    return undefined;
  }

  private static readonly ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

  private static formatTransactionDate(dateString: string): string {
    if (!dateString) return '';
    if (!IndirectTaxTransactionMapper.ISO_DATE_PATTERN.test(dateString)) {
      return dateString;
    }
    return NonTimezoneSensitiveDate.fromISO8601String(dateString).format(
      'MMM D, YYYY'
    );
  }

  private static mapStatus(apiStatus: string): TIndirectTaxTransactionStatus {
    const map: Record<string, TIndirectTaxTransactionStatus> = {
      LOCKED: TIndirectTaxTransactionStatus.LOCKED,
      UNCOMMITTED: TIndirectTaxTransactionStatus.UNCOMMITTED,
      COMMITTED: TIndirectTaxTransactionStatus.COMMITTED,
      VOIDED: TIndirectTaxTransactionStatus.VOIDED,
    };
    return map[apiStatus] ?? TIndirectTaxTransactionStatus.UNCOMMITTED;
  }

  private static mapTransactionType(
    apiType: string
  ): TIndirectTaxTransactionType {
    const map: Record<string, TIndirectTaxTransactionType> = {
      SALE: TIndirectTaxTransactionType.SALE,
      REFUND: TIndirectTaxTransactionType.REFUND,
      PURCHASE: TIndirectTaxTransactionType.PURCHASE,
      PURCHASE_REFUND: TIndirectTaxTransactionType.PURCHASE_REFUND,
    };
    return map[apiType] ?? TIndirectTaxTransactionType.SALE;
  }

  private static getSourceDisplay(
    sourcePlatform: TIndirectTaxTransactionSourcePlatform | null | undefined
  ): string {
    return IndirectTaxSourcePlatformHelper.getDisplayName(sourcePlatform);
  }

  static getSourcePlatformDisplayName(
    platform: TIndirectTaxTransactionFilterSourcePlatform
  ): string {
    if (platform === TIndirectTaxManualUploadSourcePlatform) {
      return 'Manual upload';
    }
    return ROOTFI_INTEGRATION_ENUM_TO_TEXT[platform] ?? platform;
  }

  private static computeEffectiveTaxRate(
    taxCollected: number,
    total: number
  ): string {
    const taxable = total - taxCollected;
    if (taxable === 0) return '0.00%';
    return `${((taxCollected / taxable) * 100).toFixed(2)}%`;
  }

  static mapListItemToTransaction(
    item: TIndirectTaxTransactionListItem,
    // The region of the view rendering this row (active tab / filing region).
    // Supply type is computed relative to it; omitted in region-less views
    // (e.g. the customer detail table) where supply type isn't shown.
    homeRegion?: IndirectTaxRegion
  ): TIndirectTaxTransaction {
    const region = IndirectTaxRegionHelper.getRegionFromCountryCode(
      item.destinationCountry
    );
    const isEuOrUk =
      region === IndirectTaxRegion.EUROPEAN_UNION ||
      region === IndirectTaxRegion.UNITED_KINGDOM;
    const homeIsEuOrUk =
      homeRegion === IndirectTaxRegion.EUROPEAN_UNION ||
      homeRegion === IndirectTaxRegion.UNITED_KINGDOM;

    return {
      id: item.id,
      transactionType: IndirectTaxTransactionMapper.mapTransactionType(
        item.type
      ),
      status: IndirectTaxTransactionMapper.mapStatus(item.status),
      date: IndirectTaxTransactionMapper.formatTransactionDate(item.date),
      reportingDate: item.reportingDate
        ? IndirectTaxTransactionMapper.formatTransactionDate(item.reportingDate)
        : undefined,
      source: IndirectTaxTransactionMapper.getSourceDisplay(
        item.sourcePlatform
      ),
      sourcePlatformId: item.sourcePlatformId ?? undefined,
      invoiceNumber: item.invoiceNumber ?? undefined,
      marketplace: item.marketplace ?? undefined,
      currency: item.invoiceCurrency ?? 'USD',
      amount: item.subTotal ?? 0,
      taxCollected: item.taxCollected ?? 0,
      total: item.total ?? 0,
      effectiveTaxRate: IndirectTaxTransactionMapper.computeEffectiveTaxRate(
        item.taxCollected ?? 0,
        item.total ?? 0
      ),
      customer: item.customer
        ? {
            id: item.customer.id,
            name: item.customer.name,
            email: item.customer.email,
          }
        : undefined,
      jurisdictionName: IndirectTaxTransactionMapper.getJurisdictionName(
        item.destinationState,
        item.destinationCountry
      ),
      countryOrTradeBloc:
        IndirectTaxTransactionMapper.getCountryOrTradeBlocDisplay(
          item.destinationCountry
        ),
      destinationCountryName: IndirectTaxTransactionMapper.getCountryFullName(
        item.destinationCountry
      ),
      originCountryName: IndirectTaxTransactionMapper.getCountryFullName(
        item.originCountry
      ),
      supplyType:
        homeIsEuOrUk && homeRegion
          ? IndirectTaxTransactionMapper.deriveSupplyType(
              item.originCountry,
              item.destinationCountry,
              homeRegion
            )
          : undefined,
      documentType: undefined,
      vatStatus: undefined,
      taxableAmount: isEuOrUk ? (item.subTotal ?? 0) : undefined,
      vat: isEuOrUk ? (item.taxCollected ?? 0) : undefined,
      grossAmount: isEuOrUk ? (item.total ?? 0) : undefined,
      effectiveVatRate: isEuOrUk
        ? IndirectTaxTransactionMapper.computeEffectiveTaxRate(
            item.taxCollected ?? 0,
            item.total ?? 0
          )
        : undefined,
      grossSales:
        item.type === 'SALE' || item.type === 'REFUND' ? item.total : undefined,
      grossPurchases:
        item.type === 'PURCHASE' || item.type === 'PURCHASE_REFUND'
          ? item.total
          : undefined,
      outputVat:
        item.type === 'SALE' || item.type === 'REFUND'
          ? item.taxCollected
          : undefined,
      inputVat:
        item.type === 'PURCHASE' || item.type === 'PURCHASE_REFUND'
          ? item.taxCollected
          : undefined,
    };
  }

  private static readonly JURISDICTION_TYPE_LABELS: Record<
    TIndirectTaxCalculationTaxBreakdownJurisdictionType,
    string
  > = {
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.STATE]: 'State',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.COUNTY]: 'County',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.CITY]: 'City',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.DISTRICT]: 'District',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.LOCAL]: 'Local',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.COUNTRY]: 'Country',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.STATE_OR_PROVINCE]:
      'State/Province',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.TRADEBLOC]:
      'Trade bloc',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.RESERVATION]:
      'Reservation',
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.TERRITORY]:
      'Territory',
  };

  private static formatJurisdictionType(
    type: TIndirectTaxCalculationTaxBreakdownJurisdictionType
  ): string {
    return IndirectTaxTransactionMapper.JURISDICTION_TYPE_LABELS[type] ?? type;
  }

  private static formatTaxRate(rate: number): string {
    return `${Number(rate.toFixed(4))}%`;
  }

  private static formatJurisdictionName(name: string): string {
    return toTitleCaseSentence(name).replace(
      /\(([a-z])/g,
      (_, letter: string) => `(${letter.toUpperCase()}`
    );
  }

  private static readonly JURISDICTION_TYPE_ORDER: Record<
    TIndirectTaxCalculationTaxBreakdownJurisdictionType,
    number
  > = {
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.TRADEBLOC]: 0,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.COUNTRY]: 1,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.TERRITORY]: 2,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.STATE_OR_PROVINCE]: 3,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.STATE]: 4,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.COUNTY]: 5,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.CITY]: 6,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.DISTRICT]: 7,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.LOCAL]: 8,
    [TIndirectTaxCalculationTaxBreakdownJurisdictionType.RESERVATION]: 9,
  };

  private static jurisdictionTypeRank(
    type: TIndirectTaxCalculationTaxBreakdownJurisdictionType
  ): number {
    return (
      IndirectTaxTransactionMapper.JURISDICTION_TYPE_ORDER[type] ??
      Number.MAX_SAFE_INTEGER
    );
  }

  private static mapApiTaxBreakdown(
    rows: TIndirectTaxLineItemTaxBreakdownApi[]
  ): TIndirectTaxJurisdictionBreakdown[] {
    return [...rows]
      .sort(
        (a, b) =>
          IndirectTaxTransactionMapper.jurisdictionTypeRank(
            a.jurisdiction_type
          ) -
          IndirectTaxTransactionMapper.jurisdictionTypeRank(b.jurisdiction_type)
      )
      .map((row) => ({
        jurisdiction: IndirectTaxTransactionMapper.formatJurisdictionName(
          row.jurisdiction_name
        ),
        jurisdictionType: IndirectTaxTransactionMapper.formatJurisdictionType(
          row.jurisdiction_type
        ),
        exempt: row.exempt_amount ?? 0,
        nonTaxable: row.non_taxable_amount ?? 0,
        taxable: row.taxable_amount ?? 0,
        marketplace: row.marketplace_amount ?? 0,
        taxOrFees: row.tax_amount ?? 0,
        rate: IndirectTaxTransactionMapper.formatTaxRate(row.tax_rate ?? 0),
      }));
  }

  private static mapApiTaxLogic(
    taxLogic: TIndirectTaxLineItemTaxLogicApi,
    productId?: string
  ): TIndirectTaxLineItemTaxLogic {
    return {
      taxCalculationEnabled: taxLogic.tax_calculation_enabled,
      customerTaxable: taxLogic.customer_taxable,
      productTaxable: taxLogic.product_taxable,
      productId,
    };
  }

  static mapApiLineItemToLineItem(
    item: TIndirectTaxTransactionLineItemApi
  ): TIndirectTaxTransactionLineItem {
    const productId = item.product_id ?? item.product?.product_id ?? undefined;

    return {
      id: item.id,
      productName: item.product?.product_name ?? 'N/A',
      productCode: item.product?.product_id ?? 'N/A',
      productSku: item.product?.product_sku ?? 'N/A',
      productId,
      taxCode: item.tax_code ?? 'N/A',
      quantity: item.quantity ?? 0,
      unitPrice: item.price_per_unit ?? 0,
      discount: item.discount ?? 0,
      tax: item.tax_collected ?? 0,
      lineTotal: item.amount ?? 0,
      taxIncluded: item.tax_included ?? false,
      taxBreakdown: item.tax_breakdown?.length
        ? IndirectTaxTransactionMapper.mapApiTaxBreakdown(item.tax_breakdown)
        : undefined,
      taxLogic: item.tax_logic
        ? IndirectTaxTransactionMapper.mapApiTaxLogic(item.tax_logic, productId)
        : undefined,
    };
  }

  static mapExtendedToDetail(
    transaction: TIndirectTaxApiTransactionExtended,
    lineItems: TIndirectTaxTransactionLineItemApi[] = []
  ): TIndirectTaxTransactionDetail {
    const region = IndirectTaxRegionHelper.getRegionFromCountryCode(
      transaction.customer_details?.shipping_address?.country ??
        transaction.shipping_address?.country ??
        'US'
    );

    const destinationAddr = transaction.customer_details?.shipping_address;
    const destinationAddress = destinationAddr
      ? [
          destinationAddr.address_line_1,
          destinationAddr.city,
          destinationAddr.state,
          destinationAddr.country,
          destinationAddr.postal_code,
        ]
          .filter(Boolean)
          .join(', ')
      : '';

    const originAddr = transaction.ship_from_address;
    const originAddress = originAddr
      ? [
          originAddr.address_line_1,
          originAddr.city,
          originAddr.state,
          originAddr.country,
          originAddr.postal_code,
        ]
          .filter(Boolean)
          .join(', ')
      : '';

    const isEuOrUk =
      region === IndirectTaxRegion.EUROPEAN_UNION ||
      region === IndirectTaxRegion.UNITED_KINGDOM;

    return {
      id: transaction.id,
      transactionType: IndirectTaxTransactionMapper.mapTransactionType(
        transaction.invoice_type
      ),
      status: IndirectTaxTransactionMapper.mapStatus(
        transaction.invoice_status
      ),
      date: IndirectTaxTransactionMapper.formatTransactionDate(
        transaction.transacted_at
      ),
      reportingDate: transaction.reporting_date_override
        ? IndirectTaxTransactionMapper.formatTransactionDate(
            transaction.reporting_date_override
          )
        : undefined,
      source: IndirectTaxTransactionMapper.getSourceDisplay(
        transaction.source_platform
      ),
      sourcePlatformId: transaction.source_platform_id ?? undefined,
      invoiceNumber: transaction.invoice_number ?? undefined,
      currency: transaction.invoice_currency ?? 'USD',
      amount: transaction.subtotal ?? 0,
      taxCollected: transaction.tax_collected ?? 0,
      total: transaction.total ?? 0,
      effectiveTaxRate: '0%',
      customer: transaction.customer
        ? {
            id: transaction.customer.id,
            name: transaction.customer.name,
            email: transaction.customer.email,
          }
        : undefined,
      jurisdictionName: IndirectTaxTransactionMapper.getJurisdictionName(
        destinationAddr?.state ?? '',
        destinationAddr?.country ?? 'US'
      ),
      countryOrTradeBloc:
        IndirectTaxTransactionMapper.getCountryOrTradeBlocDisplay(
          destinationAddr?.country ?? 'US'
        ),
      destinationCountryCode:
        destinationAddr?.country ?? transaction.shipping_address?.country,
      destinationCountryName: IndirectTaxTransactionMapper.getCountryFullName(
        destinationAddr?.country
      ),
      originCountryName: IndirectTaxTransactionMapper.getCountryFullName(
        originAddr?.country
      ),
      taxableAmount: isEuOrUk ? (transaction.subtotal ?? 0) : undefined,
      vat: isEuOrUk ? (transaction.tax_collected ?? 0) : undefined,
      grossAmount: isEuOrUk ? (transaction.total ?? 0) : undefined,
      taxBasis: lineItems.some((item) => item.tax_included)
        ? TIndirectTaxTaxBasis.TAX_INCLUSIVE
        : TIndirectTaxTaxBasis.TAX_EXCLUSIVE,
      filingCurrency: transaction.invoice_currency ?? 'USD',
      originAddress,
      destinationAddress,
      conversionRate: undefined,
      customerId:
        transaction?.customer_id ||
        transaction.customer_details?.customer_id ||
        undefined,
      exemptionApplied: !!transaction.exemption_type,
      exemptionReason: transaction.exemption_type ?? undefined,
      lineItems: lineItems.map(
        IndirectTaxTransactionMapper.mapApiLineItemToLineItem
      ),
      // TODO: entity name not provided by this endpoint — fetch separately if needed.
      entityName: '',
      entityCountryCode: destinationAddr?.country ?? 'US',
      filings: transaction.filings ?? [],
    };
  }
}
